import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Fingerprint, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Field, Select, Btn, ReceiptDialog } from "@/components/shared";
import { useMeta, useSubmit } from "@/pages/Services";

const TYPES = [{ v: "CW", l: "Cash Withdrawal" }, { v: "BE", l: "Balance Enquiry" }, { v: "MS", l: "Mini Statement" }];

function useAepsStatus() {
  const [s, setS] = useState(null);
  const load = () => api.get("/aeps/status").then(({ data }) => setS(data));
  useEffect(() => { load(); }, []);
  return [s, load];
}

function Scanner({ scanning }) {
  return (
    <div className={`grid h-28 w-28 place-items-center rounded-full border-2 ${scanning ? "animate-pulse border-emerald-500 bg-emerald-50" : "border-dashed border-slate-300"}`}>
      <Fingerprint className={`h-12 w-12 ${scanning ? "text-emerald-600" : "text-slate-400"}`} />
    </div>
  );
}

export function Aeps({ aadhaarPay }) {
  const meta = useMeta();
  const [status] = useAepsStatus();
  const [type, setType] = useState(aadhaarPay ? "AP" : "CW");
  const blank = { bank: "", aadhaar: "", mobile: "", amount: "" };
  const [f, setF] = useState(blank);
  const [scanning, setScanning] = useState(false);
  const { loading, receipt, setReceipt, run } = useSubmit(() => setF(blank));
  const needsAmount = type === "CW" || type === "AP";
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value.replace(k === "bank" ? /$^/ : /\D/g, "") });

  const scan = async (e) => {
    e.preventDefault();
    setScanning(true);
    await new Promise((r) => setTimeout(r, 1200));
    setScanning(false);
    run("/aeps/transaction", { ...f, txn_type: type, amount: Number(f.amount || 0) }, "Transaction successful");
  };

  const ready = status?.onboarded && status?.twofa_today;
  return (
    <div>
      <PageHeader title={aadhaarPay ? "Aadhaar Pay" : "AEPS Services"} subtitle={aadhaarPay ? "Accept merchant payments via customer Aadhaar biometric. Credited to main wallet (0.5% charge)." : "Aadhaar enabled cash withdrawal, balance enquiry & mini statement."} />
      {status && !ready && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="aeps-not-ready">
          <AlertTriangle className="h-4 w-4" /> {!status.onboarded ? "AEPS onboarding pending." : "Daily 2FA authentication pending."}
          <Link to="/aeps/onboarding" className="font-semibold underline" data-testid="goto-aeps-onboard">Complete now</Link>
        </div>
      )}
      {!aadhaarPay && (
        <div className="mb-4 inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="aeps-type-tabs">
          {TYPES.map((t) => (
            <button key={t.v} onClick={() => setType(t.v)} className={`rounded-md px-4 py-1.5 text-sm font-semibold ${type === t.v ? "bg-emerald-600 text-white" : "text-slate-600"}`} data-testid={`aeps-tab-${t.v}`}>{t.l}</button>
          ))}
        </div>
      )}
      <Card className="max-w-4xl">
        <form onSubmit={scan} className="grid gap-6 md:grid-cols-[1fr_auto]" data-testid="aeps-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Customer Bank"><Select options={meta?.banks || []} placeholder="Select Bank" value={f.bank} onChange={(e) => setF({ ...f, bank: e.target.value })} required data-testid="aeps-bank-select" /></Field>
            <Field label="Aadhaar Number"><input className="np-input font-mono" maxLength={12} value={f.aadhaar} onChange={set("aadhaar")} required data-testid="aeps-aadhaar-input" /></Field>
            <Field label="Customer Mobile"><input className="np-input" maxLength={10} value={f.mobile} onChange={set("mobile")} required data-testid="aeps-mobile-input" /></Field>
            {needsAmount && <Field label="Amount (100 - 10000)"><input className="np-input font-mono" value={f.amount} onChange={set("amount")} required data-testid="aeps-amount-input" /></Field>}
            <label className="flex items-center gap-2 text-xs text-slate-500 sm:col-span-2"><input type="checkbox" required data-testid="aeps-consent-checkbox" /> Customer consent taken for Aadhaar authentication</label>
          </div>
          <div className="flex flex-col items-center justify-center gap-3">
            <Scanner scanning={scanning} />
            <Btn variant="green" type="submit" loading={loading || scanning} disabled={!ready} data-testid="aeps-scan-finger-button">{scanning ? "Capturing…" : "Scan & Submit"}</Btn>
          </div>
        </form>
      </Card>
      <ReceiptDialog txn={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}

export function AepsOnboarding() {
  const { user } = useAuth();
  const [status, load] = useAepsStatus();
  const [f, setF] = useState({ aadhaar: user.aadhaar || "", pan: user.pan || "", shop_address: user.address || "", pincode: user.pincode || "" });
  const [loading, setLoading] = useState(false);
  const call = async (url, body) => {
    setLoading(true);
    try {
      const { data } = await api.post(url, body);
      toast.success(data.message);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div>
      <PageHeader title="AEPS Onboarding & Daily 2FA" subtitle="One-time outlet onboarding, then daily biometric authentication before first AEPS transaction." />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="1. Outlet onboarding">
          {status?.onboarded ? (
            <div className="flex items-center gap-2 text-emerald-700" data-testid="aeps-onboarded"><CheckCircle2 className="h-5 w-5" />Onboarding active</div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); call("/aeps/onboard", f); }} className="space-y-3" data-testid="aeps-onboard-form">
              <Field label="Aadhaar"><input className="np-input font-mono" maxLength={12} value={f.aadhaar} onChange={set("aadhaar")} required data-testid="onboard-aadhaar-input" /></Field>
              <Field label="PAN"><input className="np-input font-mono uppercase" maxLength={10} value={f.pan} onChange={set("pan")} required data-testid="onboard-pan-input" /></Field>
              <Field label="Shop Address"><input className="np-input" value={f.shop_address} onChange={set("shop_address")} required data-testid="onboard-address-input" /></Field>
              <Field label="Pincode"><input className="np-input" maxLength={6} value={f.pincode} onChange={set("pincode")} required data-testid="onboard-pincode-input" /></Field>
              <Btn type="submit" loading={loading} data-testid="onboard-submit-button">Activate Onboarding</Btn>
            </form>
          )}
        </Card>
        <Card title="2. Daily 2FA authentication">
          <div className="flex flex-col items-center gap-4 py-4">
            <Scanner scanning={loading} />
            {status?.twofa_today ? (
              <div className="flex items-center gap-2 text-emerald-700" data-testid="aeps-2fa-done"><CheckCircle2 className="h-5 w-5" />Authenticated for today</div>
            ) : (
              <Btn variant="green" loading={loading} disabled={!status?.onboarded} onClick={() => call("/aeps/2fa")} data-testid="aeps-2fa-button">Scan Fingerprint for 2FA</Btn>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
